import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Mail, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FlyerRequestModal({ isOpen, onClose }) {
  const [step, setStep] = useState('form'); // 'form' | 'success' | 'error'
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    flyerType: '',
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/flyer-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setStep('success');
      } else {
        setStep('error');
      }
    } catch (error) {
      console.error('Flyer request error:', error);
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form after a delay to allow exit animation
    setTimeout(() => {
      setStep('form');
      setFormData({
        flyerType: '',
        name: '',
        email: '',
        phone: '',
        organization: '',
        message: '',
      });
    }, 300);
  };

  const isFormValid = formData.flyerType && formData.name && formData.email;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="flyer-request-modal-overlay"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-[#2A2421]/60 backdrop-blur-sm" 
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg bg-[#FDFBF7] rounded-2xl shadow-2xl overflow-hidden"
            data-testid="flyer-request-modal"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              data-testid="flyer-request-close"
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-[#E8E1D9]/50 flex items-center justify-center text-[#7A736E] hover:text-[#2A2421] hover:bg-[#E8E1D9] transition-all duration-200"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            {/* Top Accent Line */}
            <div className="h-1 bg-gradient-to-r from-[#A67A5B] via-[#C59B76] to-[#A67A5B]" />

            <div className="p-8 md:p-10 max-h-[80vh] overflow-y-auto">
              {step === 'form' && (
                <form onSubmit={handleSubmit} data-testid="flyer-request-form">
                  {/* Header */}
                  <div className="text-center mb-6">
                    <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2A2421] mb-3 leading-tight">
                      Private Flyer Request
                    </h2>
                    <p className="font-sans text-sm text-[#7A736E] leading-relaxed italic">
                      Please let us know which flyer you need, and we'll send it with care.
                    </p>
                  </div>

                  {/* Flyer Type Dropdown */}
                  <div className="mb-4">
                    <label htmlFor="flyerType" className="block font-sans text-xs font-semibold text-[#2A2421] mb-2 uppercase tracking-wide">
                      Flyer Type <span className="text-[#A67A5B]">*</span>
                    </label>
                    <select
                      id="flyerType"
                      name="flyerType"
                      value={formData.flyerType}
                      onChange={handleChange}
                      required
                      data-testid="flyer-type-select"
                      className="w-full bg-white border border-[#E8E1D9] focus:border-[#A67A5B] px-4 py-3 rounded-xl outline-none transition-colors duration-300 font-sans text-sm text-[#2A2421] appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23A67A5B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center',
                      }}
                    >
                      <option value="">Select flyer type...</option>
                      <option value="ministry">Ministry</option>
                      <option value="hospital">Hospital / Child Life</option>
                    </select>
                  </div>

                  {/* Name */}
                  <div className="mb-4">
                    <label htmlFor="name" className="block font-sans text-xs font-semibold text-[#2A2421] mb-2 uppercase tracking-wide">
                      Your Name <span className="text-[#A67A5B]">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your name"
                      data-testid="flyer-name-input"
                      className="w-full bg-white border border-[#E8E1D9] focus:border-[#A67A5B] px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-sm text-[#2A2421]"
                    />
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <label htmlFor="email" className="block font-sans text-xs font-semibold text-[#2A2421] mb-2 uppercase tracking-wide">
                      Email Address <span className="text-[#A67A5B]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      data-testid="flyer-email-input"
                      className="w-full bg-white border border-[#E8E1D9] focus:border-[#A67A5B] px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-sm text-[#2A2421]"
                    />
                  </div>

                  {/* Phone */}
                  <div className="mb-4">
                    <label htmlFor="phone" className="block font-sans text-xs font-semibold text-[#2A2421] mb-2 uppercase tracking-wide">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(Optional)"
                      data-testid="flyer-phone-input"
                      className="w-full bg-white border border-[#E8E1D9] focus:border-[#A67A5B] px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-sm text-[#2A2421]"
                    />
                  </div>

                  {/* Organization */}
                  <div className="mb-4">
                    <label htmlFor="organization" className="block font-sans text-xs font-semibold text-[#2A2421] mb-2 uppercase tracking-wide">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="Name of your ministry, hospital, or organization (optional)"
                      data-testid="flyer-organization-input"
                      className="w-full bg-white border border-[#E8E1D9] focus:border-[#A67A5B] px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-sm text-[#2A2421]"
                    />
                  </div>

                  {/* Message */}
                  <div className="mb-6">
                    <label htmlFor="message" className="block font-sans text-xs font-semibold text-[#2A2421] mb-2 uppercase tracking-wide">
                      Additional Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Tell us a bit about how you plan to use this flyer (optional)"
                      data-testid="flyer-message-input"
                      className="w-full bg-white border border-[#E8E1D9] focus:border-[#A67A5B] px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-sm text-[#2A2421] resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={!isFormValid || loading}
                    data-testid="flyer-submit-btn"
                    className="w-full bg-[#A67A5B] text-white py-3.5 rounded-full font-medium tracking-wide hover:bg-[#8C654A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {loading ? 'Sending...' : 'Submit Request'}
                  </button>
                </form>
              )}

              {step === 'success' && (
                <div className="text-center py-4" data-testid="flyer-request-success">
                  {/* Success Icon */}
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                    <Check className="text-green-600" size={32} />
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-3">
                    Request Sent!
                  </h2>

                  <p className="font-sans text-base text-[#7A736E] leading-relaxed mb-4">
                    Thank you, <strong>{formData.name}</strong>!
                  </p>

                  <p className="font-sans text-sm text-[#7A736E] leading-relaxed mb-4 max-w-sm mx-auto">
                    Your <strong>{formData.flyerType === 'ministry' ? 'Ministry' : 'Hospital & Child Life'}</strong> flyer request has been received. You should receive an email with your flyer within the next few minutes.
                  </p>

                  <p className="font-sans text-xs text-[#7A736E]/60 italic mb-6">
                    Please check your spam folder if you don't see it in your inbox.
                  </p>

                  <p className="font-sans text-xs text-[#7A736E] mb-6">
                    Questions?{' '}
                    <Link to="/contact" onClick={handleClose} className="text-[#A67A5B] hover:underline font-medium">
                      Contact us here
                    </Link>
                  </p>

                  <button
                    onClick={handleClose}
                    data-testid="flyer-success-close"
                    className="bg-[#A67A5B] text-white px-8 py-3 rounded-full font-medium text-sm tracking-wide hover:bg-[#8C654A] transition-all duration-300"
                  >
                    Close
                  </button>
                </div>
              )}

              {step === 'error' && (
                <div className="text-center py-4" data-testid="flyer-request-error">
                  {/* Error Icon */}
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="text-red-600" size={32} />
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-3">
                    Unable to Send Request
                  </h2>

                  <p className="font-sans text-sm text-[#7A736E] leading-relaxed mb-4 max-w-sm mx-auto">
                    We received your request but encountered an issue sending the email.
                  </p>

                  <p className="font-sans text-sm text-[#7A736E] leading-relaxed mb-6 max-w-sm mx-auto">
                    Please{' '}
                    <Link to="/contact" onClick={handleClose} className="text-[#A67A5B] hover:underline font-medium">
                      contact us here
                    </Link>{' '}
                    and we'll send your flyer right away.
                  </p>

                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setStep('form')}
                      data-testid="flyer-error-retry"
                      className="bg-[#A67A5B] text-white px-6 py-3 rounded-full font-medium text-sm tracking-wide hover:bg-[#8C654A] transition-all duration-300"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={handleClose}
                      className="border-2 border-[#E8E1D9] text-[#7A736E] px-6 py-3 rounded-full font-medium text-sm tracking-wide hover:border-[#A67A5B] hover:text-[#A67A5B] transition-all duration-300"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
